<template>
    <div>
        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin-top: 20px;">Organization admin setup</h1>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="!succeedCreateUserCommandV1" class="container">
            <strong>Create a new organization admin</strong><br /><br />

            <div class="form-group">
                <label for="Email">Email:</label>
                <input v-model="email" type="email" class="form-control" placeholder="Work email of an admin">
                <ValidationError context="Email" :errors="validationErrors"></ValidationError>
            </div>

            <div class="form-group">
                <label for="FirstName">First name:</label>
                <input v-model="firstName" type="text" class="form-control" placeholder="First name of an admin">
                <ValidationError context="FirstName" :errors="validationErrors"></ValidationError>
            </div>

            <div class="form-group">
                <label for="LastName">Last name:</label>
                <input v-model="lastName" type="text" class="form-control" placeholder="Last name of an admin">
                <ValidationError context="LastName" :errors="validationErrors"></ValidationError>
            </div>

            <div class="form-group form-check">
                <input v-model="setInitialPassword" type="checkbox" class="form-check-input" id="setInitialPassword">
                <label class="form-check-label" for="setInitialPassword">Set initial password manually</label>
            </div>

            <div v-show="setInitialPassword">
                <div class="form-group">
                    <label for="Password">Initial password:</label>
                    <input v-model="password" type="password" class="form-control">
                    <ValidationError context="Password" :errors="validationErrors"></ValidationError>
                </div>

                <div class="form-group" v-show="password">
                    <label for="PasswordConfirmation">Initial password confirmation:</label>
                    <input v-model="passwordConfirmation" type="password" class="form-control" :class="{ 'is-invalid': passwordConfirmationInvalid }">
                    <div class="invalid-feedback">Initial password confirmation doesn't match</div>
                </div>
            </div>

            <button v-if="!invokingCreateUserCommandV1" @click="handleCreateUserCommandV1" type="button" class="btn btn-primary btn-lg" :disabled="passwordConfirmationInvalid">Create</button>
            <button v-else type="button" class="btn btn-primary btn-lg" disabled>Creating...</button>
            <br /><br />

            <div v-if="users.length">
                <hr />
                <strong>Set existing user as organization admin</strong><br /><br />

                <div class="row">
                    <div v-for="u in users" class="col-md-4 col-sm-6" style="margin-bottom: 20px;">
                        <div class="card bg-light">
                            <div class="card-header">{{u.FirstName}} {{u.LastName}}</div>
                            <div class="card-body">
                                <p class="card-text">{{u.Email}}</p>
                                <button @click="handleAssignOrganizationAdminRoleCommandV1(u)" class="btn btn-link">Set as admin</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="container">
            <h1>Congratulation!</h1>
            <h3>Organization admin account was created and password was sent on email {{email}}</h3>
            <a class="btn btn-primary btn-lg" href="/#/admin/organizations/manage" role="button">Manage organizations</a>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from 'vue'
    import { useRoute, useRouter } from 'vue-router'

    import ValidationError from '@common/Components/ValidationError.vue'
    import { useOrganizationAdminCandidatesQueryV1 } from '@Organizations/Users/queries-vue-composables'
    import
        {
            useCreateUserCommandV1,
            useAssignOrganizationAdminRoleCommandV1
        } from '@Organizations/Users/commands-vue-composables'

    // ----------------------------------
    // routing
    // ----------------------------------

    const route = useRoute()
    const router = useRouter()

    const tid = computed(() => Number(route.params.tid))

    // ----------------------------------
    // local state
    // ----------------------------------

    const email = ref('')
    const firstName = ref('')
    const lastName = ref('')
    const password = ref('')
    const passwordConfirmation = ref('')
    const setInitialPassword = ref(false)

    // ----------------------------------
    // validation
    // ----------------------------------

    const passwordConfirmationInvalid = computed(() =>
    {
        if (!setInitialPassword.value) return false
        return !!passwordConfirmation.value && passwordConfirmation.value !== password.value
    })

    // ----------------------------------
    // queries
    // ----------------------------------

    const {
        runOrganizationAdminCandidatesQueryV1,
        modelOrganizationAdminCandidatesQueryV1
    } = useOrganizationAdminCandidatesQueryV1(tid)

    const users = computed(
        () => modelOrganizationAdminCandidatesQueryV1.value?.Users ?? []
    )

    // ----------------------------------
    // create user command
    // ----------------------------------

    const {
        invokeCreateUserCommandV1,
        invokingCreateUserCommandV1,
        succeedCreateUserCommandV1,
        validationErrors,
        errorCreateUserCommandV1
    } = useCreateUserCommandV1(
        '0',
        tid,
        email,
        firstName,
        lastName,
        false,
        true,
        false,
        false,
        setInitialPassword,
        password
    )

    const {
        invokeAssignOrganizationAdminRoleCommandV1,
        succeedAssignOrganizationAdminRoleCommandV1
    } = useAssignOrganizationAdminRoleCommandV1()


    onMounted(() =>
    {
        document.title = 'Organization admin setup'
        runOrganizationAdminCandidatesQueryV1()
    })

    function handleCreateUserCommandV1()
    {
        if (passwordConfirmationInvalid.value) return
        invokeCreateUserCommandV1()
    }

    function handleAssignOrganizationAdminRoleCommandV1(user: any)
    {
        if (window.confirm(`Do you really want to assign Organization Admin role for ${user.Email}?`))
        {
            invokeAssignOrganizationAdminRoleCommandV1({id: user.Email})
        }
    }

    // ----------------------------------
    // command outcomes
    // ----------------------------------

    watch(succeedAssignOrganizationAdminRoleCommandV1, (ok) =>
    {
        if (ok)
        {
            router.push('/admin/organizations/manage')
        }
    })

    watch(errorCreateUserCommandV1, (err) => 
    {
        if (err) {
            alert(err)
        }
    })

</script>


